<template>
  <parent>
    <div class="general-wrapper h-100">
      <loading-page v-if="loading" />
      <template v-else>
        <form-wrapper>
          <template v-slot:body>
            <paper-simple>
              <input-field
                label="Default Root Object"
                label-position="top"
                :info="messages.defaultRootObject"
                @openInfo="showInfo(messages.defaultRootObject.title, messages.defaultRootObject.message)"
              >
                <b-form-input
                  v-model="distribution.defaultRootObject"
                  placeholder="Type default root object here..."
                />
              </input-field>
              <single-tag
                label-name="Alternate Domain Names (CNAMEs)"
                :info="messages.cnames"
                placeholder="Specify CNAMEs"
                icon-name="Moreoptions"
                :show-single-item="false"
                :options="optionsData"
                :tags="computedAlternateDomainNames"
                :show-protocol="true"
                :is-ssl="distribution.sslCertificateType === 2"
                @clicked-option="clikedData"
                @tags="tagsData"
                @deletedTags="handleDeleteTags"
              />
            </paper-simple>
            <paper-simple>
              <custom-input-radio
                v-model="distribution.sslCertificateType"
                label="SSL Certificate"
                :info="sslInfo"
                :options="getOptions(sslCertificateOptions)"
                @openInfo="showInfo(sslTitle, sslMessage)"
              />
              <input-field
                v-if="distribution.sslCertificateType === 4"
                class="my-1 mx-50"
                label="Select Certificate"
                label-position="top"
              >
                <custom-select
                  v-model="distribution.sslCertificateId"
                  :options="formattedSniCertificates"
                  placeholder="Select a certificate"
                />
              </input-field>
            </paper-simple>

            <paper-simple>
              <div class="d-flex my-1">
                <b-form-checkbox
                  v-model="distribution.imageOptimizer"
                  class="toggle-spacing"
                  switch
                />
                <div class="">

                  <span class="">Image Optimizer</span>
                  <a
                    @click="
                      showInfo(messages.imageOptimizer.title, messages.imageOptimizer.message)
                    "
                  >
                    <GjIcon
                      class="ml-1"
                      name="Info"
                    />
                  </a>
                </div>
              </div>
              <div
                v-if="distribution.imageOptimizer"
                class="border rounded p-1 my-2 bg-secondary-light"
              >
                <div class="mb-1">
                  <span class="text-secondary font-weight-bold">Image Optimizer Default Settings</span>
                  <a
                    @click="
                      showInfo(messages.imageOptimizerDefaultSettings.title, messages.imageOptimizerDefaultSettings.message)
                    "
                  >
                    <GjIcon
                      class="ml-1"
                      name="Info"
                    />
                  </a>
                </div>

                <div class="col-12 col-lg-12 d-flex flex-wrap">
                  <DotTextDisplay
                    v-for="item in imageDefaultSettings"
                    :key="item.labelTitle"
                    class="col-lg-4"
                    :label-title="item.labelTitle"
                    :info-text="item.infoText"
                  />
                </div>

              </div>
              <input-field
                v-if="distribution.wafSettingOptions"
                label="Web Application Firewall"
                label-position="top"
                :text-right="false"
                :info="messages.waf"
                @openInfo="showInfo(messages.waf.title, messages.waf.message)"
              >
                <custom-select
                  v-if="distribution.wafSettingOptions.length > 0"
                  v-model="distribution.wafSetting"
                  :options="getOptions(distribution.wafSettingOptions)"
                />
              </input-field>
            </paper-simple>

            <paper-simple>
              <custom-input-radio
                v-model="distribution.stateId"
                label="Distribution State"
                :info="messages.distributionState"
                :options="stateIdOptions"
                @openInfo="showInfo(messages.distributionState.title, messages.distributionState.message)"
              />
            </paper-simple>

            <paper-simple>
              <input-field
                label="Comment"
                label-position="top"
              >
                <div class="w-100">
                  <b-form-input v-model="distribution.comment" />
                  <custom-alert
                    icon-name="Info"
                    items-center
                    class="mt-1"
                  >
                    {{ messages.comment.message }}
                  </custom-alert>
                </div>
              </input-field>
            </paper-simple>

          </template>

          <template v-slot:footer>
            <b-button
              variant="bg-white"
              @click="$router.push({name: 'distribution-general', params: { id }})"
            >
              Cancel
            </b-button>
            <button-loading
              variant="bg-primary"
              class="ml-16"
              :spinning="updatingDistribution"
              @click="onUpdate"
            >
              Update
            </button-loading>
          </template>

        </form-wrapper>
        <info
          v-if="openInfo"
          :title="infoTitle"
          :message="infoMessage"
          @close="openInfo = false"
        />

        <CNamePopup
          v-if="showCnameInfo"
          :domain-name="distribution.domainName"
          :ttl="3600"
          class="custom-class"
          :cname="cname"
          @close="showCnameInfo = false"
          @add="showCnameInfo = false"
        />
      </template>
    </div>
  </parent>
</template>
<script>
import messages from '../../messages'
import CustomInputRadio from '@/components/form/CustomInputRadio.vue'
import redirectError from '@/services/redirectError'
import SingleTag from '@/components/tags/SingleTag.vue'
import CNamePopup from './components/CNamePopup.vue'

export default {
  name: 'GeneralEditor',
  components: {
    CustomInputRadio,
    SingleTag,
    CNamePopup,
    DotTextDisplay: () => import('@/components/info/DotTextDisplay.vue'),
  },
  data() {
    return {
      selected: null,
      messages,
      openInfo: false,
      showCnameInfo: false,
      infoTitle: '',
      infoMessage: '',
      distribution: {},
      wafSetting: false,
      customSslCertificateOptions: [
        { value: null, text: 'Select' },
      ],
      id: '',
      stateIdOptions: [
        { value: 0, text: 'Disable' },
        { value: 1, text: 'Enable' },
      ],
      imageDefaultSettings: [
        {
          labelTitle: 'AutoWebp',
          infoText: 'Yes',
        },
        {
          labelTitle: 'Default WEBp quality',
          infoText: '100',
        },
        {
          labelTitle: 'Default JPEG format',
          infoText: 'No',
        },
        {
          labelTitle: 'Default JPEG quality',
          infoText: '100',
        },
        {
          labelTitle: 'Maximum input dimensions',
          infoText: '24,000 px',
        },
        {
          labelTitle: 'Maximum input file size',
          infoText: '100mb - 104,857,600 bytes',
        },
        {
          labelTitle: 'Maximum output dimensions',
          infoText: '8192 px',
        },
        {
          labelTitle: 'Allow AVIF?',
          infoText: 'Yes',
        },
      ],
      optionsData: [
        {
          id: 1,
          name: 'Show Info',
          iconName: 'Show',
        },
        // {
        //   id: 2,
        //   name: 'Edit',
        //   iconName: 'Edit',
        // },
        {
          id: 3,
          name: 'Delete',
          iconName: 'Delete',
        },
      ],
      tagsData: [],
      cname: '',
    }
  },
  computed: {
    dist() {
      return this.$store.state.cdn.distribution
    },
    computedAlternateDomainNames: {
      get() {
        if (this.distribution.alternateDomainNames === '' || this.distribution.alternateDomainNames === null) {
          return []
        }
        return this.distribution.alternateDomainNames.split(',')
      },
      set(val) {
        this.distribution.alternateDomainNames = val
      },
    },
    sniCertificates() {
      return this.$store.state.cdn.sniCertificates
    },
    formattedSniCertificates() {
      return this.sniCertificates.map(cert => ({
        value: cert.id,
        text: this.formatCertificateLabel(cert),
        cert,
      }))
    },
    sslCertificateOptions() {
      return this.distribution.sslCertificateOptions
    },
    updatingDistribution() {
      return this.$store.state.cdn.updatingDistribution
    },
    loading() {
      return this.$store.state.cdn.gettingDistribution
    },
    sslTitle() {
      return this.distribution.sslCertificateType === 2 ? this.messages.instantSSL.title : this.messages.noSSL.title
    },
    sslMessage() {
      // Define the message for the <info> component based on the sslCertificateType value
      return this.distribution.sslCertificateType === 2 ? this.messages.instantSSL.message : this.messages.noSSL.message
    },
    sslInfo() {
      return this.distribution.sslCertificateType === 2 ? this.messages.instantSSL : this.messages.noSSL
    },
  },
  created() {
    this.id = this.$route.params.id
    this.getDistribution(this.id)
    this.fetchSniCertificates()
  },
  methods: {
    formatCertificateLabel(cert) {
      const additional = cert.additionalNames ? ` (${cert.additionalNames})` : ''
      return `#${cert.id} - ${cert.domainName}${additional} - ${cert.certificateStatus}`
    },

    getDistribution(id) {
      this.$store.dispatch('cdn/getDistribution', id)
        .then(() => {
          this.distribution = this.dist
        })
        .catch(err => redirectError(err, this.$router, this.$route.path))
    },
    formatCnames() {
      if (this.computedAlternateDomainNames.length === 0) return null
      const updatedCnames = this.computedAlternateDomainNames.map(cname => cname.trim().replace(/^https?:\/\//, ''))
      return updatedCnames.join(',')
    },
    onUpdate() {
      if (this.distribution.sslCertificateType === 4 && !this.distribution.sslCertificateId) {
        this.errorToast('Please select a certificate before updating.')
        return
      }
      this.$store.dispatch('cdn/updateDistribution', {
        distributionId: this.distribution.distributionId,
        stateId: this.distribution.stateId,
        priceClassId: this.distribution.priceClassId,
        sslCertificateId: this.distribution.sslCertificateType === 4
          ? this.distribution.sslCertificateId
          : null,
        sslCertificateType: this.distribution.sslCertificateType,
        imageOptimizer: this.distribution.imageOptimizer,
        securityPolicyId: this.distribution.securityPolicyId,
        defaultRootObject: this.distribution.defaultRootObject,
        comment: this.distribution.comment,
        alternateDomainNames: this.formatCnames(),
        wafSetting: this.distribution.wafSetting,
      }).then(() => {
        this.successToast('Distribution was updated')
        this.$router.push({ name: 'distribution-general', params: { id: this.id } })
      }).catch(error => { this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while updating certificate.') })
    },
    clikedData(data, tag) {
      if (data.name === 'Show Info') {
        this.showCnameInfo = true
        this.cname = tag
      }
    },
    handleDeleteTags(updatedTags) {
      this.distribution.alternateDomainNames = updatedTags.join(',')
    },
    async fetchSniCertificates() {
      try {
        await this.$store.dispatch('cdn/fetchSniCertificates')
      } catch (error) {
        const message = error?.response?.data?.errors?.[0] ?? 'Failed to fetch sni certificates.'
        this.errorToast(message)
      }
    },
    showInfo(title, info) {
      this.openInfo = true
      this.infoTitle = title
      this.infoMessage = info
    },
  },
}
</script>
<style lang="scss" scoped>
.general-wrapper {
  .toggle-spacing {
    margin-top: -15px;
  }
  ::v-deep {
    .form-wrapper {
      &__body {
        > div {
          margin: auto;
        }
      }
    }
    .card.paper {
      .card-body {
        padding-bottom: 0;
      }
    }
    .info-assistant {
      margin-left: auto;
    }
  }
}

.custom-class::v-deep {
  .popup {
    width: 580px;
      @media screen and (max-width: 600px) {
        width: auto;
    }

    .popup__body {
      padding: 17px !important;
    }
  }
}
</style>
